<template>
    <brick-settings-popup
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_ABTest_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_ABTest_Delete_Body'),
            brickTitle: `${resource('JourneyComposer_BrickSettingsABTest_Title')} ${resource('JourneyComposer_BrickSettingsBase_Title')}`
        }"
        :brick="brick"
        :name.sync="newName"
        :invalid="$v.$invalid"

        @save="saveSettings()"
        @delete="$emit('delete')"
        @hidden="$emit('hidden')"
        @display-errors="displayErrors()"
    >
        <settings-item icon="fas fa-columns">
            <template #label>
                <label class="control-label" for="ab-attributes">
                    {{resource('JourneyComposer_ABTest_Attributes_DisplayName')}}
                </label>
            </template>

            <template #input>
                <v-select class="form-control"
                    label="label"
                    inputId="ab-attributes"

                    multiple
                    clearable
                    :closeOnSelect="false"

                    v-model="settings.attributes"
                    :options="selectableAttributes"

                    :disabled="!canWrite"
                />
            </template>
        </settings-item>
        
        <hr />
        
        <div class="row form-group">
            <id-dispenser :elements="settings.routes">
                <template v-slot="{ idsPerElement }">
                    <div 
                        v-for="(route, index) in settings.routes" 
                        class="col-12 ab-route" 
                        :key="idsPerElement.get(route)"
                    >
                        <div class="row form-group">
                            <a-b-test-route class="col-12"
                                            :color.sync="route.color"
                                            :percentage.sync="route.percentage"
                                            :description.sync="route.description"
                            />

                            <div class="offset-8 col-4">
                                <button @click.prevent="removeRoute(route)"
                                        class="btn btn-outline-danger btn-icon-left w-100"
                                        :disabled="!canWrite"
                                >
                                    <i class="fa fa-minus" />
                                    {{resource('JourneyComposer_ABTest_DeleteRoute')}}
                                </button>
                            </div>

                            <div v-if="index !== settings.routes.length - 1" class="col-12">
                                <hr />
                            </div>
                        </div>
                    </div>
                </template>
            </id-dispenser>
            
            <div class="offset-8 col-4">
                <button @click.prevent="addRoute"
                    class="btn btn-outline-secondary btn-icon-left w-100"
                    :disabled="!canWrite"
                >
                    <i class="fa fa-plus" />
                    {{resource('JourneyComposer_ABTest_AddRoute')}}
                </button>
            </div>
        </div>
        
        <div v-if="$v.$invalid && $v.$dirty" class="row">
            <div class="offset-4 col-8">
                <error-message
                    v-if="!$v.routesTotalPercentage.isHundredPercent"
                    :message="isHundredPercentValidationMessage" 
                />
            </div>
        </div>
    </brick-settings-popup>
</template>

<script>
import snowplow from '@modules/JourneyBuilder/snowplow';
import {sizes} from '@core/Components/Util/Modal.vue';
import api from "@modules/JourneyBuilder/api";
import sprintf from 'sprintf';

import brickSettingsMixin from '../Mixins/brick-settings'
import {validationMixin} from 'vuelidate';

import BrickSettingsPopup from '../BrickSettingsPopup';
import ABTestRoute from "./ABTestRoute";
import ErrorMessage from "@core/Components/Errors/ErrorMessage";
import IdDispenser from "@core/Components/Util/IdDispenser";

const removeArrayItem = (arr, item) => {
    const itemIndex = arr.indexOf(item);
    if(itemIndex !== -1){
        arr.splice(itemIndex, 1);
    }
};

export default {
    name: "ABTestSettings",
    components: {
        BrickSettingsPopup,
        ABTestRoute,
        ErrorMessage,
        IdDispenser
    },
    mixins: [brickSettingsMixin, validationMixin],
    async created() {
        //Ensure reactivity of attributes/routes collections
        //prevent modifications from bubbling by virtue of shallowCloning them
        this.settings = {
            //Preserve values set from external locations
            ...this.settings,
            
            //Explicitly declare values which we'll be modifying
            attributes: (this.settings.attributes || []).map(c => ({ ...c })),
            routes: (this.settings.routes || []).map(c => ({ ...this.createEmptyRoute(), ...c }))
        }
        
        this.availableAttributes = await api.getAudienceAttributes();
    },
    methods: {
        save(){
            snowplow.trackStructuredEvent('changed-ab-test-settings');
            this.saveSettings();
        },
        displayErrors(){
            this.$v.$touch();
        },
        removeRoute(condition){
            removeArrayItem(this.settings.routes, condition);
        },
        addRoute(){
            this.settings.routes.push(this.createEmptyRoute());
        },
        createEmptyRoute(){
            return {
                //Attempt to take leftover percentage, max 100, min 1
                percentage: Math.max(1, Math.min(100, 100 - this.routesTotalPercentage)),
                color: '',
                description: undefined
            };
        }
    },
    validations: {
        routesTotalPercentage: {
            isHundredPercent: value => value === 100
        }
    },
    computed: {
        routesTotalPercentage(){
            return this.settings.routes.reduce((pct, route) => pct + route.percentage, 0);
        },
        isHundredPercentValidationMessage(){
            return sprintf(
                this.resource('JourneyComposer_ABTest_RoutesTotalPercentage_NotOneHundredPercent_ErrorMessage'), 
                this.routesTotalPercentage
            );
        },
        selectableAttributes(){
            //For small arrays, Array.prototype.indexOf should be faster than creating a new Set(...)
            const currentAttributeIds = this.settings.attributes.map(attr => attr.id);
            
            //Don't clutter the UI with already selected attributes
            return this.availableAttributes.filter(
                attr => currentAttributeIds.indexOf(attr.id) === -1
            );
        }
    },
    data: () => ({
        modalSize: sizes.large,
        availableAttributes: []
    })
}
</script>
